import { useContext, useEffect, useState } from 'react';
import { NumberField, LoadingDots, Link, FlosIcon, Message } from '@flos/react-ui';
import { SupManWidget as SupManWidgetType, FormValues, FormConfig } from '@topdanmark-onlinesolutions/claim-form-types';
import { ClaimFormSubmitFieldValue } from '../../../types/claim-form-submit-field-value';
import { SummaryRowTemplate } from '../../../helpers/templates';
import { getClaimFormSubmitValue } from '../../../helpers/getClaimFormSubmitValue';
import { getElementFromElementId } from '../../../helpers/getElementFromElementId';
import { getValue } from '../../values/Get';
import CustomerVehicle from '../CustomerVehicle/CustomerVehicle';
import useAppState from '../../../state/appState';
import { supplierParams, supplierService } from '../../../services/suppliers';
import { BranchData, WorkshopData, SupManWidgetScenarioType, CarbucksReservation } from '../../../types/services';
import { ConfigContext } from '../../../context/ConfigContext';
import WorkshopList from './WorkshopList';
import { WORKSHOP_NOTE, NO_SHOP_FOUND, WORKSHOPS_SERVICE_UNAVAILABLE, CARBUCKS_SERVICE_UNAVAILABLE } from './label';
import { getZipcode, isPrivateUser } from '../../../helpers/topContext';
import ConfirmationOverlay from '../../custom/SelectWorkshop/ConfirmationOverlay';
import './style.scss';
import { trackInfo, trackInteract } from '../../../track-by-submit/common/tracking';
import { SelectWorkshopOptionType } from '../SelectWorkshop/SelectWorkshop';
import CarbucksBooking, { CloseStatus } from './CarbucksBooking';
import { CarbucksService } from '../../../services/carBucks';

type Status = 'loading' | 'ok' | 'error' | 'carbucksError';

const customerVehicleElementID = 'registrationNumber';
const OWN_SHOP_LABEL = 'Jeg kontakter selv et værksted';
const OWN_SHOP_VALUE_HUMAN = 'Du har valgt eget værksted';

// just a global copy of the workshops loaded, required in order to include the list in the payload when the form is submitted
// (the workshopList state object is only accessible inside the SupManWidget component itself, not in the form submit function)
let workshops: BranchData[];
let cbWorkshops: BranchData[];

const SupManWidget = ({ elementId, uspTexts }: SupManWidgetType) => {
    const appConfig = useContext(ConfigContext);
    const formValues = useAppState((state) => state.formValues);
    const setFormValue = useAppState((state) => state.setFormValue);
    //cb
    const damageSize = formValues.damageSize?.value;
    const hasFiles = formValues.fileUpload
        ? formValues.fileUpload.files &&
          Array.isArray(formValues.fileUpload?.files) &&
          formValues.fileUpload.files.length > 0
        : false;

    const value = SupManWidget.getValueOrDefault(formValues, elementId);
    const customerVehicle = CustomerVehicle.getValueOrDefault(formValues, customerVehicleElementID);

    const brand =
        customerVehicle.displayValue === 'Andet' ||
        customerVehicle.displayValue === 'Bil' ||
        customerVehicle.displayValue === ''
            ? formValues.carBrand
            : customerVehicle.displayValue;

    const formConfig = useAppState((state) => state.formConfig);

    const topContextZipcode = getZipcode();

    let defaultZipCode = topContextZipcode;
    if (value.zipcode) {
        defaultZipCode = value.zipcode;
    }

    const [status, setStatus] = useState<Status>('ok');
    const [zipcode, setZipcode] = useState<string>(defaultZipCode);
    const [workshopList, setWorkshopList] = useState<BranchData[]>(value.branch);
    const [regularWorkshops, setRegularWorkshops] = useState<number>(0);
    const [carbucksWorkshops, setCarbucksWorkshops] = useState<number>(0);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [showCarbucksText, setShowCarbucksText] = useState(false);

    const [isCarbucksModalOpen, setIsCarbucksModalOpen] = useState(false);
    const [selectedWorkshop, setSelectedWorkshop] = useState<BranchData | null>(null);
    const [carbucksReservation, setCarbucksReservation] = useState<CarbucksReservation | null>(null);

    // If zip code is not equal from state the selection should be resetted
    const zipCodeIsNotEqual = value.zipcode !== zipcode;

    const defaultValue = {
        zipcode,
        brand,
        branch: null
    };

    useEffect(() => {
        let scenario: SupManWidgetScenarioType;

        if (zipCodeIsNotEqual) {
            setFormValue(elementId, { ...defaultValue, ...{ scenario: 'shopsFoundAndShopTypeChosenButNoShopChosen' } });
        }

        const getWorkshopList = async () => {
            const params: supplierParams = {
                brand,
                q: zipcode,
                businessArea: 'AUTO',
                damageSize
            };

            setStatus('loading');
            const response: WorkshopData | null = await supplierService(appConfig, params);
            if (response?.branches) {
                if (response.branches.length === 0) {
                    scenario = 'noShopsFound';
                    setFormValue(elementId, { ...defaultValue, ...{ scenario, status: 'ok' } });
                }

                const regularWorkshops: BranchData[] = response.branches.filter(
                    (branch: BranchData) => branch.category === 'REGULAR'
                );
                setRegularWorkshops(regularWorkshops.length);

                const carbucksWorkshops: BranchData[] = response.branches.filter(
                    (branch: BranchData) => branch.category === 'QUICK_FIX'
                    //branch.name.toLowerCase().includes('carbucks')
                );

                setCarbucksWorkshops(carbucksWorkshops.length);

                if (isPrivateUser()) {
                    let trackingValue = '';
                    const hasCarbucksWorkshops = carbucksWorkshops.length > 0;
                    const useRegularWorkshops = !hasFiles && hasCarbucksWorkshops;
                    cbWorkshops = carbucksWorkshops;

                    if (!hasFiles && damageSize) {
                        setShowCarbucksText(false);
                        setWorkshopList(regularWorkshops);
                        trackingValue = 'cb-not-shown-no-files';
                    } else if (damageSize === 'small' && hasCarbucksWorkshops) {
                        setShowCarbucksText(true);
                        setWorkshopList(carbucksWorkshops);
                        trackingValue = 'cb-shown-small-damage';
                    } else {
                        setShowCarbucksText(false);
                        setWorkshopList(response.branches);

                        if (useRegularWorkshops) {
                            trackingValue = 'cb-not-shown-no-files';
                        } else if (hasCarbucksWorkshops) {
                            trackingValue = 'cb-shown-big-damage';
                        } else if (damageSize === 'small' && !hasCarbucksWorkshops) {
                            trackingValue = 'cb-not-shown-small-damage-no-cb-workshops';
                        } else {
                            trackingValue = 'cb-not-shown-big-damage-no-cb-workshops';
                        }
                    }
                    formConfig && trackInfo(formConfig, [{ key: 'carbucks-visibility-reason', value: trackingValue }]);
                } else {
                    // Remove QUICK_FIX if user role is not PRIVATE
                    // const onlyRegularShops = response.branches.filter((branch) => branch.category === 'REGULAR');
                    setWorkshopList(response.branches);
                    workshops = regularWorkshops;
                    cbWorkshops = carbucksWorkshops;
                }
                workshops = regularWorkshops;
                cbWorkshops = carbucksWorkshops;
                setStatus('ok');
            } else {
                scenario = 'serviceUnavailable';
                setStatus('error');
                setFormValue(elementId, { ...defaultValue, ...{ scenario, status: 'error' } });
            }
        };
        if (zipcode) {
            getWorkshopList();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appConfig, brand, elementId, zipcode]);

    // load in the carbucks reservation from the saved form valules (if one exist)
    useEffect(() => {
        if (formConfig && !carbucksReservation) {
            const formValue = getValue(formConfig, formValues, elementId);

            if (formValue && formValue.carbucksReservation) {
                // the formValues collection is loosely typed, so we need to map the fields one at a time here
                setCarbucksReservation({
                    createdAt: formValue.carbucksReservation.createdAt,
                    updatedAt: formValue.carbucksReservation.updatedAt,
                    deletedAt: formValue.carbucksReservation.deletedAt,
                    carbucksGUID: formValue.carbucksReservation.carbucksGUID,
                    carbucksUUID: formValue.carbucksReservation.carbucksUUID,
                    carbucksName: formValue.carbucksReservation.carbucksName,
                    orderGUID: formValue.carbucksReservation.orderGUID,
                    orderUUID: formValue.carbucksReservation.orderUUID,
                    reservationGUID: formValue.carbucksReservation.reservationGUID,
                    reservationUUID: formValue.carbucksReservation.reservationUUID,
                    reservationStartDatetime: formValue.carbucksReservation.reservationStartDatetime,
                    reservationEndDatetime: formValue.carbucksReservation.reservationEndDatetime
                });
            }
        }
    }, [formConfig, formValues, elementId, carbucksReservation]);

    const onSelectWorkshopList = (workshop: BranchData, isSelected: boolean) => {
        const scenario: SupManWidgetScenarioType = 'shopsFoundAndShopTypeChosenAndShopChosen';

        const isCarbucksWorkshop = workshop.category === 'QUICK_FIX';

        if (!isSelected || isCarbucksWorkshop) {
            setSelectedWorkshop(workshop);

            if (isCarbucksWorkshop && isPrivateUser()) {
                formConfig &&
                    trackInfo(formConfig, [
                        { key: 'carbucks-workshop-selected', value: workshop.branchId },
                        { key: 'workshop-book-appointment-clicked', value: true }
                    ]);

                setIsCarbucksModalOpen(true);
            } else {
                // The workshop is not selected when clicking on workshop
                setFormValue(elementId, {
                    status,
                    zipcode,
                    scenario,
                    brand,
                    selectedBranchId: workshop.branchId,
                    branch: workshop,
                    regularWorkshops,
                    carbucksWorkshops
                });
                setFormValue('selectWorkshop', { scenario }); //update scenario so that there is only one submission of supmanwidget component
            }
        } else {
            // The workshop is unselected
            setSelectedWorkshop(null);
            setFormValue(elementId, {
                ...value,
                scenario: 'shopsFoundAndShopTypeChosenButNoShopChosen',
                selectedBranchId: '',
                branch: undefined,
                regularWorkshops: 0,
                carbucksWorkshops: 0
            });
        }
    };

    // Triggered once the user can made a Carbucks booking reservation
    const onCarbucksBookingReservation = (reservation: CarbucksReservation, workshop: BranchData | null) => {
        const scenario: SupManWidgetScenarioType = 'shopsFoundAndShopTypeChosenAndShopChosen';

        setCarbucksReservation(reservation);

        setStatus('ok');
        setFormValue(elementId, {
            status,
            zipcode,
            scenario,
            brand,
            selectedBranchId: workshop?.branchId,
            branch: workshop,
            carbucksReservation: reservation,
            regularWorkshops,
            carbucksWorkshops
        });
        setFormValue('selectWorkshop', { scenario });

        setIsCarbucksModalOpen(false);
    };

    const onCloseCarbucksModal = (status: CloseStatus) => {
        setIsCarbucksModalOpen(false);

        let scenario: SupManWidgetScenarioType;

        switch (status) {
            case 'timeslotsApiFailed':
            case 'reservationApiFailed':
                // Handle Carbucks Timeslots API failure and Reservation API failure
                scenario = 'carbucksServiceUnavailable';
                setStatus('carbucksError');
                setFormValue(elementId, { ...defaultValue, ...{ scenario, status: 'carbucksError' } });
                break;
            case 'ok':
                // Handle successful closure - nothing special needed
                break;
        }
    };

    const onChangeValue = (action?: SelectWorkshopOptionType) => {
        setFormValue(elementId, { ...defaultValue, ...{ scenario: action } });
    };

    const onZipcodeChanged = (zipCode: string) => {
        setFormValue(elementId, { ...value, zipCode });
    };

    if (!formConfig) {
        return <></>;
    }

    return (
        <>
            {showCarbucksText ? (
                <>
                    <h4 className="page-header">Du har mulighed for hurtig reparation</h4>
                    <div className="page-subtext">
                        Vi kan hjælpe dig med at udbedre din skade på kun to timer via vores værkstedspartner Carbucks.
                    </div>
                </>
            ) : (
                <>
                    <h4 className="page-header">
                        Du har særlig mulighed for at bruge vores anbefalede fordelsværksteder
                    </h4>
                    <div className="page-subtext">
                        Her klarer vi alt det praktiske, og værkstedet kontakter dig inden for to timer. Du får også
                        lånebil, rengøring, bilvask og udvidet garanti på tre år.
                        <br />
                    </div>
                </>
            )}

            <div className="select-workshop">
                <p>Vælg et værksted nær dig:</p>
                <NumberField
                    label="Postnummer"
                    required
                    maxLength={4}
                    type="text"
                    onValueChange={(values) => {
                        const value = values.value;
                        if (value.match(/^\d{4}$/)) {
                            if (zipcode !== value) {
                                setZipcode(value);
                                onZipcodeChanged(value);
                                setStatus('loading');
                            }
                        }
                    }}
                    value={zipcode}
                />

                {status === 'loading' && (
                    <div className="u-spacing-inset-5">
                        <LoadingDots centered={false} />
                    </div>
                )}

                {status === 'error' && <Message theme="warning">{WORKSHOPS_SERVICE_UNAVAILABLE}</Message>}

                {/* We should allow the customer to try again in case of a 'carbucksError', so we should render this part also if an error occured on previous try */}
                {['ok', 'carbucksError'].includes(status) && workshopList && (
                    <div>
                        <>
                            <WorkshopList
                                workshopList={workshopList}
                                selectedBranchId={value.selectedBranchId}
                                carbucksReservation={carbucksReservation}
                                onSelectWorkshopList={(workshop: BranchData, isSelected: boolean) =>
                                    onSelectWorkshopList(workshop, isSelected)
                                }
                            />

                            {workshopList.length > 0 && WORKSHOP_NOTE}

                            {status === 'ok' && (
                                <div className="own-workshop-link">
                                    Nej tak, jeg ønsker selv at kontakte et værksted -{' '}
                                    <Link
                                        onClick={() => {
                                            trackInteract(formConfig, [
                                                { key: 'workshop-selection-overlay', value: 'true' }
                                            ]);
                                            setIsModalOpen(true);
                                        }}
                                    >
                                        klik her
                                    </Link>
                                </div>
                            )}

                            {value.scenario === 'customerDoesNotWantShops' && (
                                <div className="own-workshop-note">
                                    <FlosIcon shape="check" size={24} />
                                    <span>{OWN_SHOP_LABEL}</span>
                                </div>
                            )}
                        </>
                    </div>
                )}

                {status === 'carbucksError' && (
                    <div style={{ marginTop: '1rem' }}>
                        <Message theme="warning">{CARBUCKS_SERVICE_UNAVAILABLE}</Message>
                    </div>
                )}
            </div>

            {isPrivateUser() && (
                <CarbucksBooking
                    isModalOpen={isCarbucksModalOpen}
                    selectedWorkshop={selectedWorkshop}
                    existingReservation={carbucksReservation}
                    onCloseClick={onCloseCarbucksModal}
                    onBookingReservation={onCarbucksBookingReservation}
                />
            )}

            <ConfirmationOverlay
                open={isModalOpen}
                close={() => {
                    setIsModalOpen(false);
                }}
                onSkipWorkshopSelection={(action) => {
                    trackInteract(formConfig as FormConfig, [
                        {
                            key: `workshop-selection-action-${
                                action === 'customerDoesNotWantShops' ? 'skip-workshop' : 'choose-workshop'
                            }`,
                            value: 'true'
                        }
                    ]);
                    onChangeValue(action as SelectWorkshopOptionType);
                }}
            />
        </>
    );
};

SupManWidget.ResumePage = (formConfig: FormConfig, formValues: FormValues, elementId: string) => {
    const element = getElementFromElementId(formConfig, elementId) as SupManWidgetType;
    const { summaryCaption } = element;
    const value = getValue(formConfig, formValues, elementId);
    const { branch, scenario, carbucksReservation } = value;

    if (value === undefined) {
        return <></>;
    }

    let displayValue;

    if (branch !== null) {
        if (carbucksReservation) {
            displayValue = [
                <p>
                    {branch.name}
                    <br />
                    {CarbucksService.prettyFormatReservationDateTime(carbucksReservation.reservationStartDatetime)}
                </p>,
                WORKSHOP_NOTE
            ];
        } else {
            displayValue = [<p>{branch.name}</p>, WORKSHOP_NOTE];
        }
    } else if (scenario === 'customerDoesNotWantShops') {
        displayValue = OWN_SHOP_LABEL;
    } else {
        switch (scenario) {
            case 'serviceUnavailable':
                displayValue = [WORKSHOPS_SERVICE_UNAVAILABLE];
                break;
            case 'carbucksServiceUnavailable':
                displayValue = [CARBUCKS_SERVICE_UNAVAILABLE];
                break;
            default:
                displayValue = [NO_SHOP_FOUND];
        }
    }

    return <SummaryRowTemplate elementId={elementId} caption={summaryCaption} value={displayValue} />;
};

SupManWidget.getValueSubmit = (
    formValues: FormValues,
    elementId: string,
    caption: string
): ClaimFormSubmitFieldValue => {
    const valueObj = SupManWidget.getValue(formValues, elementId);
    const valueStr = SupManWidget.getValueHuman(formValues, elementId);
    const newWidget = !!formValues.damageSize; // todo - update once the time booking is implemented

    const { scenario, brand: carBrand, zipcode: searchQuery, branch, status } = valueObj;

    const valueId = {
        scenario,
        carBrand,
        componentState: status
    };

    let branchData = {
        branchId: '',
        branchName: '',
        noShopsAction: '',
        searchQuery,
        chosenShopType: '',
        newWidget: newWidget,
        branchReservationId: '',
        branchOrderId: '',
        carbucksShops: [] as any[],
        regularShops: [] as any[]
    };

    if (scenario === 'customerDoesNotWantShops') {
        branchData = {
            ...branchData,
            noShopsAction: 'customerDoesNotWantToChooseBetweenPresentedOptions'
        };
    }

    if (branch !== null) {
        const { branchId, name, category } = branch;
        const { carbucksReservation } = valueObj;
        branchData = {
            newWidget: newWidget,
            branchId,
            branchName: name,
            noShopsAction: '',
            searchQuery,
            chosenShopType: category === 'REGULAR' ? 'regular' : 'carbucks',
            branchReservationId: carbucksReservation?.reservationGUID || '',
            branchOrderId: carbucksReservation?.orderGUID || '',
            carbucksShops: cbWorkshops,
            regularShops: workshops
        };
    } else {
        // also include the list of repairshops when customer does not select one (for BA purposes)
        branchData.regularShops = workshops;
        branchData.carbucksShops = cbWorkshops;
    }

    return getClaimFormSubmitValue('supmanwidget', elementId, caption, valueStr, { ...valueId, ...branchData });
};

SupManWidget.getValueHuman = (formValues: FormValues, elementId: string) => {
    const value = SupManWidget.getValue(formValues, elementId);
    const { branch, scenario } = value;
    if (branch !== null) {
        const { name } = branch;
        return `Værksted\n${name}`;
    }

    if (scenario === 'customerDoesNotWantShops') {
        return OWN_SHOP_VALUE_HUMAN;
    }
    return '';
};

SupManWidget.getValueOrDefault = (formValues: FormValues, elementId: string): any => {
    const valueObj: any = SupManWidget.getValue(formValues, elementId);

    if (valueObj === undefined) {
        return {
            status: '',
            zipcode: '',
            scenario: '',
            brand: '',
            branch: null
        };
    }

    return valueObj;
};

SupManWidget.isValid = (formConfig: FormConfig, formValues: FormValues, elementId: string) => {
    const valueObject = getValue(formConfig, formValues, elementId);

    if (!valueObject) {
        return false;
    }
    if (
        valueObject.scenario !== 'shopsFoundAndShopTypeChosenButNoShopChosen' &&
        valueObject.scenario !== 'shopsNotYetSearchedFor'
    ) {
        return true;
    }

    return false;
};

SupManWidget.getValue = (formValues: FormValues, elementId: string) => {
    return formValues[elementId];
};

export default SupManWidget;
